import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators } from "./redux/sagas/funnelChart";
import SectionContainer from "../../../components/sectionContainer/sectionContainer";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import SectionToggle from "../../../components/sectionToggle/sectionToggle";
import { DatePickerFilter } from "./smartComponents/DatePickerFilter";
import { CustomDateRanges } from "./smartComponents/CustomDateRangeFilter";
import { LaserFunnelGraph } from "./smartComponents/FunnelGraph";
import { IntroOfferFunnelGraph } from "./smartComponents/IntroOfferFunnelGraph";
import { ReferralBreakdownTable } from "./smartComponents/ReferralBreakdownTable";
import { StartFromFilter } from "./smartComponents/StartFromFilter";
import { AveragesFilter } from "./smartComponents/AveragesFilter";
import { AverageGroup } from "./smartComponents/AverageGroup";
import { GroupObjectsFilter } from "./smartComponents/GroupObjectsFilter";
import { LocationsAverageFilter } from "./smartComponents/LocationsAverageFilter";
import KBLearnMore from "../../../components/learnMore/KBLearnMore";
import { ILEARN_URLS } from "../../../constants/ilearnUrls";
import { addSticky } from "../../../utils/addSticky";
import { FutureAppointmentsToggle } from "./smartComponents/FutureBookingsToggle";
import { LooseConversionsToggle } from "./smartComponents/LooseConversionsToggle";
import { UpdateButton } from "./smartComponents/UpdateButton";
import { IntroOfferDataPoints } from "./smartComponents/IntroOfferDataPoints";
import { DataPoints } from "./smartComponents/DataPoints";
import { ProspectTypesFilter } from "./smartComponents/ProspectTypesFilter";
import { OptInFilter } from "./smartComponents/OptInFilter";
import { ContactMethodFilter } from "./smartComponents/ContactMethodFilter";
import { FirstContactTypeFilter } from "./smartComponents/FirstContactTypeFilter";
import { ExcludeEnrolledToggle } from "./smartComponents/ExcludeEnrolledToggle";
import { IntroOfferFilter } from "./smartComponents/IntroOfferFilter";
// import AIAgent from "../../ai";

export const FunnelGraphModule = (props) => {
  const dispatch = useDispatch();
  let showLaserNetworkAverages = useSelector(
    (state) => state.laser.currentUser.showLaserNetworkAverages
  );

  let isLoading = useSelector((state) => state.laser.isLoading);
  let tablesLoading = useSelector((state) => state.laser.tablesLoading);
  let initialRequest = useSelector((state) => state.laser.initialRequest);

  let { data } = useSelector((state) => state.laser.referralTable.referrals_table.tableInfo);

  let startFrom = useSelector((state) => state.laser.selectedFilters.startFrom.id);
  let isIntroOfferSelected = startFrom == "intro_offer";

  const filtersStacked = startFrom === "leads";

  let title = useSelector((state) =>
    isIntroOfferSelected
      ? state.laser.funnelIntroOffers.chart.label
      : state.laser.funnelChartData.chart.title
  );

  useEffect(() => {
    if (!initialRequest) {
      dispatch(actionCreators.fetchData(props));
    }
  }, []);

  useEffect(() => {
    if (!isLoading) {
      addSticky();
    }
  }, [isLoading]);

  const renderChartFilters = () => {
    return (
      <div
        className={
          showLaserNetworkAverages
            ? "filters-stacked with-average"
            : filtersStacked
            ? "filters-stacked"
            : ""
        }
      >
        {/** Select Filters */}
        <div data-testid="filters" className="filters">
          <div className="top-filters">
            <div>
              <IntroOfferFilter />
              <ProspectTypesFilter showProspectTypesFilter={props.showProspectTypesFilter} />
              <OptInFilter />

              <div className="select-box">
                <p className="offers-title">Date Range</p>
                <CustomDateRanges />
              </div>

              <div className="date-select-box select-box multi" style={{ width: "286px" }}>
                <p className="offers-title">Date</p>
                <DatePickerFilter />
              </div>
            </div>

            <div className="start-from-wrapper">
              <FirstContactTypeFilter />
              <ContactMethodFilter />

              {showLaserNetworkAverages ? (
                <>
                  <div className="select-box" style={{ width: "150px" }}>
                    <p className="offers-title">Averages</p>
                    <AveragesFilter />
                  </div>

                  <AverageGroup />

                  <GroupObjectsFilter />

                  <LocationsAverageFilter />
                </>
              ) : null}
            </div>
          </div>

          <UpdateButton showFutureAppointment={props.showFutureAppointment} />
        </div>

        {/** Switch Filters */}
        <div className="filters">
          <div>
            <FutureAppointmentsToggle showFutureAppointment={props.showFutureAppointment} />

            <LooseConversionsToggle />

            <ExcludeEnrolledToggle />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="funnel-chart-module" className="funnel-chart-module">
      <SectionToggle
        autoHeight
        title={props.moduleName + " start from"}
        customClass="laser withSticky"
        isLoading={isLoading}
        learnMore={
          <>
            <StartFromFilter />
            <KBLearnMore
              title={props.moduleName}
              description="This report shows how prospects move through the key stages of your sales funnel, highlighting where attention is needed to support stronger conversions. These insights help you focus on the most impactful stages of the funnel and improve overall sales performance."
              kbUrl={ILEARN_URLS.salesActivity.laser}
            />
          </>
        }
        content={
          <div className="flex flex-col">
            {/* Filters */}
            <div id="sticky-select-bar" className="edit-selections-bar">
              {renderChartFilters()}
            </div>
            {tablesLoading ? (
              <LoadingAnimation />
            ) : (
              <div className="flex flex-col" style={{ paddingTop: "50px" }}>
                {/* Number summaries */}
                {isIntroOfferSelected ? <IntroOfferDataPoints /> : <DataPoints />}

                {/* Graph section */}
                <div style={{ paddingTop: 25, float: "left" }}>
                  <SectionContainer title={title} style={{ padding: "0 100px 0 0" }}>
                    {isIntroOfferSelected ? <IntroOfferFunnelGraph /> : <LaserFunnelGraph />}
                  </SectionContainer>
                </div>
                {/* Table section */}
                <div className="section-group flex" style={{ paddingTop: 50 }}>
                  <SectionContainer title="" rootClassName="referral-breakdown-container">
                    {data.length > 0 ? <ReferralBreakdownTable /> : null}
                  </SectionContainer>
                </div>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};
